import type { CSSProperties, FC, ReactNode } from 'react'
import type { ChartAnimation } from '@/components/charts/chart-utils'

import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { niceCeil } from '@/components/charts/chart-utils'

export interface BarSeries {
    name: string
    values: Array<number>
    color: string
}

interface BarChartProps {
    title: string
    series: Array<BarSeries>
    labels: Array<string>
    height?: number
    stacked?: boolean
    max?: number
    gridLines?: number
    showLabels?: boolean
    labelEvery?: number
    formatValue?: (value: number) => string
    formatAxis?: (value: number) => string
    interactive?: boolean
    animation?: ChartAnimation
    delay?: number
    highlightIndex?: number
    surface?: 'light' | 'dark'
    className?: string
}

const defaultFormat = (value: number): string => value.toLocaleString('en-US')

export const BarChart: FC<BarChartProps> = ({
    title,
    series,
    labels,
    height = 180,
    stacked = false,
    max,
    gridLines = 4,
    showLabels = true,
    labelEvery = 1,
    formatValue = defaultFormat,
    formatAxis,
    interactive = true,
    animation = 'inview',
    delay = 0,
    highlightIndex,
    surface = 'light',
    className
}): ReactNode => {
    const [active, setActive] = useState<number | null>(null)
    const isDark = surface === 'dark'

    const ceiling = useMemo(() => {
        if (max) return max

        const peak = stacked
            ? Math.max(...labels.map((_, index) => series.reduce((sum, item) => sum + (item.values[index] ?? 0), 0)))
            : Math.max(...series.flatMap(item => item.values))

        return niceCeil(peak * 1.04)
    }, [max, stacked, labels, series])

    const ticks = Array.from({ length: gridLines + 1 }, (_, index) => {
        const ratio = index / gridLines

        return { top: ratio * 100, value: ceiling * (1 - ratio) }
    })

    const dimmed = (index: number): boolean => {
        if (active !== null) return active !== index
        if (highlightIndex !== undefined) return highlightIndex !== index

        return false
    }

    return (
        <figure
            className={cn('relative', animation === 'autoplay' && 'chart-autoplay', className)}
            data-chart={animation === 'manual' ? undefined : ''}
            data-inview={animation === 'autoplay' ? 'true' : undefined}
        >
            <div className='relative' style={{ height }}>
                {ticks.map(tick => (
                    <div
                        key={tick.top}
                        aria-hidden='true'
                        className={cn(
                            'absolute inset-x-0 border-t',
                            tick.top === 100 ? (isDark ? 'border-cream/20' : 'border-burgundy/15') : 'border-dashed',
                            tick.top !== 100 && (isDark ? 'border-cream/10' : 'border-burgundy/[0.08]')
                        )}
                        style={{ top: `${tick.top}%` }}
                    >
                        {formatAxis && tick.top !== 100 && (
                            <span className={cn('absolute -top-[15px] left-0 text-[10px] tabular-nums', isDark ? 'text-cream/45' : 'text-stone')}>
                                {formatAxis(tick.value)}
                            </span>
                        )}
                    </div>
                ))}

                <div
                    className={cn('absolute inset-0 grid items-end gap-[3px] sm:gap-1.5', formatAxis && 'pt-1')}
                    style={{ gridTemplateColumns: `repeat(${labels.length}, minmax(0, 1fr))` }}
                    onPointerLeave={() => setActive(null)}
                >
                    {labels.map((label, index) => {
                        const total = series.reduce((sum, item) => sum + (item.values[index] ?? 0), 0)
                        const barDelay = `${delay + index * 45}ms`

                        return (
                            <div
                                key={`${label}-${index}`}
                                className={cn('relative flex h-full items-end justify-center transition-opacity duration-300', dimmed(index) && 'opacity-45')}
                                onPointerEnter={interactive ? () => setActive(index) : undefined}
                                onPointerDown={interactive ? () => setActive(index) : undefined}
                            >
                                {stacked ? (
                                    <div
                                        data-bar=''
                                        className='chart-grow-y flex w-full max-w-7 flex-col-reverse gap-[2px]'
                                        style={{ height: `${(total / ceiling) * 100}%`, '--chart-delay': barDelay } as CSSProperties}
                                    >
                                        {series.map((item, seriesIndex) => (
                                            <div
                                                key={item.name}
                                                className={cn('w-full', seriesIndex === series.length - 1 && 'rounded-t-[4px]')}
                                                style={{ height: `${total ? ((item.values[index] ?? 0) / total) * 100 : 0}%`, backgroundColor: item.color }}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className='flex h-full w-full max-w-9 items-end justify-center gap-[2px]'>
                                        {series.map(item => (
                                            <div
                                                key={item.name}
                                                data-bar=''
                                                className='chart-grow-y w-full rounded-t-[4px]'
                                                style={{
                                                    height: `${((item.values[index] ?? 0) / ceiling) * 100}%`,
                                                    backgroundColor: item.color,
                                                    '--chart-delay': barDelay
                                                } as CSSProperties}
                                            />
                                        ))}
                                    </div>
                                )}

                                {interactive && active === index && (
                                    <div
                                        role='status'
                                        className={cn(
                                            'pointer-events-none absolute z-10 mb-2 min-w-[132px] rounded-xl border border-burgundy/10 bg-ivory/95 px-3 py-2 text-left shadow-[0_12px_32px_-16px_rgb(57_26_24/0.45)] backdrop-blur',
                                            index < labels.length / 3 ? 'left-0' : index > (labels.length * 2) / 3 ? 'right-0' : 'left-1/2 -translate-x-1/2'
                                        )}
                                        style={{ bottom: `${Math.min(56, (stacked ? total : Math.max(...series.map(item => item.values[index] ?? 0))) / ceiling * 100)}%` }}
                                    >
                                        <p className='font-mono text-[10px] uppercase tracking-[0.14em] text-stone'>{label}</p>

                                        {series.map(item => (
                                            <p key={item.name} className='mt-1 flex items-center gap-2 text-xs text-burgundy'>
                                                <span aria-hidden='true' className='size-2 rounded-full' style={{ backgroundColor: item.color }} />
                                                <span className='text-stone-dark'>{item.name}</span>
                                                <span className='ml-auto font-medium tabular-nums'>{formatValue(item.values[index] ?? 0)}</span>
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>

            {showLabels && (
                <div
                    aria-hidden='true'
                    className='mt-2 grid gap-[3px] sm:gap-1.5'
                    style={{ gridTemplateColumns: `repeat(${labels.length}, minmax(0, 1fr))` }}
                >
                    {labels.map((label, index) => (
                        <span
                            key={`${label}-${index}`}
                            className={cn('text-center text-[10px] tabular-nums', isDark ? 'text-cream/45' : 'text-stone')}
                        >
                            {index % labelEvery === 0 || index === labels.length - 1 ? label : ''}
                        </span>
                    ))}
                </div>
            )}

            <figcaption className='sr-only'>{title}</figcaption>

            <table className='sr-only'>
                <thead>
                    <tr>
                        <th scope='col'>Period</th>
                        {series.map(item => <th key={item.name} scope='col'>{item.name}</th>)}
                    </tr>
                </thead>

                <tbody>
                    {labels.map((label, index) => (
                        <tr key={`${label}-${index}`}>
                            <th scope='row'>{label}</th>
                            {series.map(item => <td key={item.name}>{formatValue(item.values[index] ?? 0)}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </figure>
    )
}
